import { UtilsService } from "@/services/utils/utils.service";
import { authorizedAPI } from "@/config/axios.config";

const utils = new UtilsService();

export type DeploymentStatus =
    | 'PENDING_DEPLOYMENT'
    | 'PROVISIONING'
    | 'PROVISION_FAILED'
    | 'DEPLOYED'
    | 'SUSPENDED'
    | 'TERMINATED';

export interface DomainHealthResult {
    domain: string;
    ok: boolean;
    status?: number;
    error?: string;
}

export interface DeployResult {
    deployment: Deployment;
    healthReport: DomainHealthResult[];
}

export interface Deployment {
    id: string;
    subscriptionId: string;
    ipAddress: string;
    frontendDomain: string;
    backendDomain: string;
    filesDomain?: string | null;
    status: DeploymentStatus;
    sshUser?: string;
    defaultUsername?: string | null;
    defaultPassword?: string | null;
    deployedAt?: string | null;
    createdAt: string;
    updatedAt: string;
    subscription?: {
        id: string;
        status: string;
        user?: {
            id: string;
            email: string;
            firstName?: string;
            lastName?: string;
        };
        company?: {
            id: string;
            name: string;
        };
        plan?: {
            id: string;
            name: string;
            displayName: string;
            priceInCents: number;
        };
    };
}

export interface CreateDeploymentDto {
    subscriptionId: string;
    ipAddress: string;
    frontendDomain: string;
    backendDomain: string;
    filesDomain?: string;
}

export interface UpdateDeploymentDto {
    ipAddress?: string;
    frontendDomain?: string;
    backendDomain?: string;
    filesDomain?: string | null;
    // PROVISIONING and DEPLOYED are absent deliberately — both are reached only
    // through their own actions. PROVISION_FAILED is settable so an admin can
    // reset a failed deployment back to PENDING_DEPLOYMENT.
    status?:
    | 'PENDING_DEPLOYMENT'
    | 'PROVISION_FAILED'
    | 'SUSPENDED'
    | 'TERMINATED';
}

export interface DeployDeploymentDto {
    defaultUsername: string;
    defaultPassword: string;
}

export interface SubscriptionForDeployment {
    id: string;
    status: string;
    user?: {
        id: string;
        email: string;
        firstName?: string;
        lastName?: string;
    };
    company?: {
        id: string;
        name: string;
    };
    plan?: {
        id: string;
        name: string;
        displayName: string;
    };
}

class DeploymentService {
    async getAll(): Promise<Deployment[]> {
        return utils.handleApiRequest<Deployment[]>(() =>
            authorizedAPI.get("/deployments")
        );
    }

    async getById(id: string): Promise<Deployment> {
        return utils.handleApiRequest<Deployment>(() =>
            authorizedAPI.get(`/deployments/${id}`)
        );
    }

    async create(data: CreateDeploymentDto): Promise<Deployment> {
        return utils.handleApiRequest<Deployment>(() =>
            authorizedAPI.post("/deployments", data)
        );
    }

    async update(id: string, data: UpdateDeploymentDto): Promise<Deployment> {
        return utils.handleApiRequest<Deployment>(() =>
            authorizedAPI.patch(`/deployments/${id}`, data)
        );
    }

    async delete(id: string): Promise<{ message: string }> {
        return utils.handleApiRequest<{ message: string }>(() =>
            authorizedAPI.delete(`/deployments/${id}`)
        );
    }

    /**
     * Calls the deploy endpoint directly (not via handleApiRequest) so a 422
     * health-check failure rejects and surfaces the per-domain report through
     * the Axios error's response body.
     */
    async deploy(id: string, data: DeployDeploymentDto): Promise<DeployResult> {
        const response = await authorizedAPI.post<DeployResult>(
            `/deployments/${id}/deploy`,
            data,
        );
        return response.data;
    }

    async getAvailableSubscriptions(): Promise<SubscriptionForDeployment[]> {
        return utils.handleApiRequest<SubscriptionForDeployment[]>(() =>
            authorizedAPI.get("/deployments/available-subscriptions")
        );
    }
}

export const deploymentService = new DeploymentService();
